<template>
  <v-card class="bg-pwt_snow" elevation="0" flat>
    <!-- compliance and inspection summary -->
    <v-card-text class="pb-0">
      <VehicleIssuesSummaryCard />
    </v-card-text>
    <!-- fleet info card section -->
    <v-card-text>
      <FleetLogoCard class="mb-5" />
    </v-card-text>
    <!-- members section -->
    <v-card flat class="bg-pwt_snow">
      <v-card-title> Members </v-card-title>
      <!-- members section: search bar -->
      <v-card-text>
        <v-text-field
          v-model="searchQuery"
          label="Search fleet members"
          outlined
          clearable
          @input="filterMembers"
          bg-color="transparent"
        >
          <template v-if="can_invite" v-slot:append-inner>
            <CustomSolidButton narrow @click.stop="() => (show_invite_dialog = true)" >
              + Invite members
            </CustomSolidButton>
          </template>
        </v-text-field>
      </v-card-text>
      <!-- members section: list row -->
      <v-card-text class="d-flex ga-2">
        <MemberCard v-if="currentUser" :user="currentUser" />
        <MemberCard
          v-for="(user, index) in filteredUsers"
          :key="'member-card-' + index"
          :user="user"
        />
      </v-card-text>
    </v-card>
    <!-- saved trips section -->

    <!-- vehicles section -->
    <v-card flat class="bg-pwt_snow">
      <v-card-title>
        <v-row no-gutters align="center">
          <span> Fleet vehicles </span>
          <v-spacer />
          <v-btn v-if="smAndDown" icon @click="navigateToAllVehicles">
            <v-icon color="grey-lighten-1">mdi-dots-vertical</v-icon>
          </v-btn>
          <CustomTextButton v-else @click="navigateToAllVehicles"> View all </CustomTextButton>
        </v-row>
      </v-card-title>
      <v-card-text>
        <span v-if="!vehicleList.length" class="py-5">
          You don't have any vehicles in your fleet
        </span>
        <v-row no-gutters v-else>
          <v-card
            flat
            v-for="(vehicle, index) in vehicleList"
            :key="'vehicle-' + index"
            :style="smAndDown ? 'width: 100%' : 'width: 50%'"
            class="pa-md-1 py-1 overflow-visible"
          >
            <DashboardVehicleListCard
              :vehicle="vehicle"
              @click="() => navigateToVehicleDetails(vehicle)"
            />
          </v-card>
        </v-row>
        <v-row no-gutters v-if="vehicleList.length">
          <v-spacer />
          <CustomSolidButton @click="showBookingDialog = true" class="mt-3">
            Book fleet vehicles
          </CustomSolidButton>
        </v-row>
        <BookingPremiumFeatureDialog
          :activator="showBookingDialog"
          @close="showBookingDialog = false"
        />
      </v-card-text>
    </v-card>
    <!-- locations section -->
    <v-card flat class="bg-pwt_snow">
      <v-card-title>
        <v-row no-gutters align="center">
          <span> Fleet locations </span>
          <v-spacer />
          <CustomTextButton v-if="mdAndUp" @click="navigateToAllLocations"> View all </CustomTextButton>
        </v-row>
      </v-card-title>
      <v-card-text>
        <span v-if="!fleetLocationsList.length" class="py-5">
          You don't have any saved locations
        </span>

        <v-row no-gutters>
          <v-col
            cols="12"
            md="6"
            v-for="(location, index) in fleetLocationsList"
            :key="'location-card-' + index"
            :class="index % 2 === 0 ? 'pr-0  pr-md-2' : 'pl-0  pl-md-2'"
            class="mb-3"
          >
            <LocationDisplayCard
              :location="location"
              @click="() => navigateToLocationDetails(location)"
            />
          </v-col>
        </v-row>
        <CustomSolidButton v-if="smAndDown" class="mt-3" @click="navigateToAllLocations" full-width>
          See all fleet locations
        </CustomSolidButton>
      </v-card-text>
    </v-card>
    <!-- invite dialog -->
    <GenericDialog :activator="show_invite_dialog" @close="close_invite_dialog" small>
      <v-card-title> Invite User </v-card-title>
      <v-card-text>
        <v-text-field
          v-model="invite_email"
          label="Email"
          outlined
          clearable
          :error-messages="email_validation_error"
          @change="clear_invite_errors"
        />
        <v-alert v-if="invite_error" type="error" class="mt-2">
          {{ invite_error }}
        </v-alert>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <CustomOutlinedButton @click="close_invite_dialog"> Cancel </CustomOutlinedButton>
        <CustomSolidButton
          @click="invite_user"
          :disabled="!invite_email.trim() || !!email_validation_error || !!invite_error"
        >
          Invite
        </CustomSolidButton>
      </v-card-actions>
    </GenericDialog>
  </v-card>
</template>

<script lang="ts" setup>
import { createInvite } from '@/api/vtnz-backend/invitations.ts'
import CustomOutlinedButton from '@/components/ui-elements/buttons/custom-buttons/CustomOutlinedButton.vue'
import CustomSolidButton from '@/components/ui-elements/buttons/custom-buttons/CustomSolidButton.vue'
import CustomTextButton from '@/components/ui-elements/buttons/custom-buttons/CustomTextButton.vue'
import type SavedLocation from '@/domains/locations/savedLocation.ts'
import { useSavedLocationStore } from '@/domains/locations/savedLocationStore.ts'
import { useUserStore } from '@/domains/user/userStore.ts'
import type { Vehicle } from '@/domains/vehicles/types.ts'
import { useVehicleStore } from '@/domains/vehicles/vehicleStore.ts'
import { useDashboardNavStore } from '@/stores/dashboardNavStore.ts'
import { useFeatureStore } from '@/stores/featureStore.ts'
import { computed, ref } from 'vue'
import { useDisplay } from 'vuetify'
import BookingPremiumFeatureDialog from '../../dialog-wrappers/BookingPremiumFeatureDialog.vue'
import GenericDialog from '../../dialog-wrappers/GenericDialog.vue'
import LocationDisplayCard from '../locations/LocationDisplayCard.vue'
import DashboardVehicleListCard from '../vehicles/DashboardVehicleListCard.vue'
import VehicleIssuesSummaryCard from '../vehicles/VehicleIssuesSummaryCard.vue'
import FleetLogoCard from './FleetLogoCard.vue'
import MemberCard from './MemberCard.vue'

const { smAndDown, mdAndUp } = useDisplay()

const showBookingDialog = ref(false)

const userStore = useUserStore()
const currentUser = computed(() => userStore.getCurrentUser)
const otherUsers = computed(() => {
  const allUsers = userStore.getUsers
  return allUsers.filter((user) => user.id !== currentUser.value?.id)
})

const vehicleStore = useVehicleStore()
const vehicleList = computed(() => {
  const returnList: Vehicle[] = []
  const fullList = [...vehicleStore.getConnectedVehicles, ...vehicleStore.getCustomVehicles]

  fullList.forEach((vehicle, index) => {
    if (index < (smAndDown ? 3 : 4)) returnList.push(vehicle)
  })

  return returnList
})

const savedLocationsStore = useSavedLocationStore()
const fleetLocationsList = computed(() => savedLocationsStore.getSavedLocations)

const dashboardNavStore = useDashboardNavStore()

const navigateToLocationDetails = (location: SavedLocation) => {
  dashboardNavStore.showLocationDetails(location.localId) ;
}

const navigateToAllLocations = () => {
  dashboardNavStore.displayedContent = 'fleet-locations'
}

const navigateToVehicleDetails = (vehicle: Vehicle) => {
  dashboardNavStore.showVehicleDetails(vehicle.id)
}

const navigateToAllVehicles = () => {
  dashboardNavStore.displayedContent = 'fleet-vehicles'
}

// ---------------------------------------------------------------------------- //
// ------------------------------- SEARCH LOGIC ------------------------------- //
// ---------------------------------------------------------------------------- //

const searchQuery = ref('')
const filterMembers = () => {}
const filteredUsers = computed(() => {
  if (!searchQuery.value.trim()) return otherUsers.value
  const query = searchQuery.value.toLowerCase()
  return otherUsers.value.filter((user) => {
    const displayName = user.fullName.toLowerCase() || ''
    const email = user.email.toLowerCase() || ''
    return displayName.includes(query) || email.includes(query)
  })
})

// ---------------------------------------------------------------------------- //
// ------------------------------- INVITE LOGIC ------------------------------- //
// ---------------------------------------------------------------------------- //

const featureStore = useFeatureStore()

const can_invite = computed(() => {
  // only fleet managers can invite users
  return featureStore.can('invite-users')
})

const show_invite_dialog = ref(false)

const invite_email = ref('')
const email_validation_error = ref<string | null>(null)
const invite_error = ref<string | null>(null)

const invite_user = async () => {
  clear_invite_errors()
  if (!can_invite.value) {
    invite_error.value = 'You do not have permission to invite users to this fleet.'
    return
  }
  // validation for email format could be added here before making the API call
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  if (!emailRegex.test(invite_email.value.trim())) {
    email_validation_error.value = 'Please enter a valid email address.'
    return
  }
  try {
    await createInvite(invite_email.value)
    // if successful, close dialog and reset state
    close_invite_dialog()
  } catch (error) {
    invite_error.value = 'Failed to send invite. Please check the email and try again.'
  }
}

const clear_invite_errors = () => {
  invite_error.value = null
  email_validation_error.value = null
}

const close_invite_dialog = () => {
  show_invite_dialog.value = false
  invite_email.value = ''
  clear_invite_errors()
}
</script>
